import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  filter, reduce, concat,
  map, difference, intersection,
  find, first,
} from 'lodash';
import { Dropdown, MenuItem } from 'react-bootstrap';

import { getTablePreference } from '../../../App/AppReducer';

import GodTable, { CUSTOM_FIELD_TYPES } from '../../../../components/god-table';
import {
  dateComparator, stringComparator,
} from '../../../../components/table-cells/helpers';
import AccountNameRenderer from '../../../../components/table-cells/AccountNameRenderer';
import MoneyRenderer from '../../../../components/table-cells/MoneyRenderer';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import CheckboxRenderer, { ALL_SUBMIT, PARTIAL_SUBMIT, NONE_SUBMIT } from '../opportunity-table/CheckboxRenderer';
import CheckboxHeaderRenderer from '../opportunity-table/CheckboxHeaderRenderer';
import ActionRenderer from '../opportunity-table/ActionRenderer';
import ContactRenderer from '../opportunity-table/ContactRenderer';
import WarningRenderer from '../opportunity-table/WarningRenderer';
import LoadingRenderer from '../opportunity-table/LoadingRenderer';
import StatusRowComponent from './total-stats';
import SubmittedRenderer from './SubmittedRenderer';
import LogModal from '../log-modal';

import styles from '../opportunity-table/styles.css';

const FILTER_OPTIONS = [
  { value: null, label: 'All Accounts' },
  { value: 'submitted', label: 'Submitted Accounts' },
  { value: 'unsubmitted', label: 'Not Submitted Accounts' },
];

const STATUS_COLUMN_ID = '__STATUS__';

class AccountTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: null,
      dynamicColumnIds: [],
      loggedAccount: null,
    };

    this.hanldeToggleAll = this.hanldeToggleAll.bind(this);

    this.showDispositionsDetail = this.showDispositionsDetail.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.handleCellClicked = this.handleCellClicked.bind(this);
    this.checkboxValueGetter = this.checkboxValueGetter.bind(this);
    this.productColumnValueGetter = this.productColumnValueGetter.bind(this);
    this.contactColumnValueGetter = this.contactColumnValueGetter.bind(this);
    this.handleProductCellClicked = this.handleProductCellClicked.bind(this);
    this.handleContactCellClicked = this.handleContactCellClicked.bind(this);
    this.handleRequestSelectAccount = this.handleRequestSelectAccount.bind(this);
    this.handleRequestDeselectAccount = this.handleRequestDeselectAccount.bind(this);
    this.handleFilterSelect = this.handleFilterSelect.bind(this);
    this.getRowClass = this.getRowClass.bind(this);
    this.handleRequestShowLogs = this.handleRequestShowLogs.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const current = this.props.editingAccount || {};
    const next = nextProps.editingAccount || {};
    if (current.account_id !== next.account_id && !next.account_id) {
      this.refresh();
    } else if (this.props.countSelected !== nextProps.countSelected) {
      if (this.gridApi) {
        this.gridApi.redrawRows({ force: true });
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onGridReady(params) {
    const { tableColumns } = this.props;
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    const validColumns = filter(tableColumns, col => col.tag === 'products' || col.tag === 'contacts');
    const defaultColumns = [STATUS_COLUMN_ID, 'action', 'checkbox', 'product-selected', 'product-available', 'contact-selected'];
    const dynamicColumnIds = concat(map(validColumns, '_id'), defaultColumns);
    this.setState({ dynamicColumnIds });
  }

  getRowClass(params) {
    const r = this.productColumnValueGetter(params);
    const r2 = this.contactColumnValueGetter(params);

    if (r && r.loaded) {
      if (r.allSubmitted) {
        return styles.disabledRow;
      } else if (r.isSelected) {
        if (r.selectedProductCount > 0 && r2.selectedContactCount > 0) {
          return styles.selectedRow;
        }
        return styles.errorRow;
      }
    }
    return null;
  }

  getAccountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      enablePivot: true,
      enableRowGroup: true,
      field: 'name',
      comparator: stringComparator,
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        return {
          component: 'accountNameRenderer',
          params: {
            enableAutoHeight: true,
            column,
          },
        };
      },
    };
  }

  getAmountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'amount',
      type: 'numericColumn',
      cellRenderer: 'moneyRenderer',
      cellRendererParams: {
        column,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }
  
  getOwnerColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      enablePivot: true,
      enableRowGroup: true,
      field: 'owner.name',
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        return {
          component: 'saleRepsNameRenderer',
          params: {
            enableAutoHeight: true,
            column,
          },
        };
      },
    };
  }

  getStatusColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      type: 'numericColumn',
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        return {
          component: 'submittedRenderer',
          params: {
            showDispositionsDetail: this.showDispositionsDetail,
          },
        };
      },
      cellRendererParams: {
        column: {
          ...column,
          description: 'Count of times this account has been submitted to TruVoice for a customer experience analysis.',
        },
      },
      valueGetter: (params) => {
        if (params && params.data) {
          const { tracking } = params.data;
          const loaded = tracking !== undefined;
          if (!loaded) {
            return null;
          }
          let internalTrackings = [];
          if (loaded) {
            internalTrackings = tracking.trackings || [];
          }
          const submitted = internalTrackings.length > 0;
          if (submitted) {
            return internalTrackings.length;
          }
          return 0;
        }
        return null;
      },
    };
  }

  getCheckColumn() {
    const { disabled, masterCheckboxStatus } = this.props;
    return {
      colId: 'checkbox',
      headerName: '',
      pinned: 'left',
      width: 35,
      field: 'account_id',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      suppressMovable: true,
      suppressMenu: true,
      lockPosition: true,
      headerComponent: 'checkboxHeaderRenderer',
      onCellClicked: this.handleCellClicked,
      headerComponentParams: {
        menuIcon: 'test',
        disabled,
        status: masterCheckboxStatus,
        requestToggleAll: this.hanldeToggleAll,
      },
      cellRendererParams: {
      },
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        const { selectedAccounts } = this.props;
        const account = params.data;
        const selectedAccount = selectedAccounts[account.account_id];
        const isSelected = selectedAccount !== undefined;
        let isValid = false;
        if (isSelected) {
          const r1 = this.productColumnValueGetter(params);
          const r2 = this.contactColumnValueGetter(params);
          if (r1 && r1.loaded) {
            isValid = (r1.selectedProductCount > 0 && r2.selectedContactCount > 0);
          }
        }

        return {
          component: 'checkboxRenderer',
          params: {
            isSelected,
            disabled,
            isValid,
          },
        };
      },
      valueGetter: this.checkboxValueGetter,
    };
  }

  getActionColumn() {
    const { disabled } = this.props;
    return {
      colId: 'action',
      headerName: 'Action',
      pinned: 'right',
      width: 140,
      field: 'opportunity_id',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      suppressMovable: true,
      suppressMenu: true,
      lockPosition: true,
      cellRendererSelector: (params) => {
        if (!params || !params.data) {
          return null;
        }
        const { selectedAccounts } = this.props;
        const selectedObject = selectedAccounts[params.data.account_id];
        return {
          component: 'actionRenderer',
          params: {
            disabled,
            isSelected: selectedObject !== undefined && selectedObject !== null,
            selectedObject,
            requestEdit: this.props.requestEditAccount,
            requestSelect: this.handleRequestSelectAccount,
            requestDeselect: this.handleRequestDeselectAccount,
            requestShowLogs: this.handleRequestShowLogs,
          },
        };
      },
    };
  }

  getProductColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      hide: !column.is_visible,
      marryChildren: true,
      resizable: false,
      headerClass: 'text-center',
      cellRendererParams: {
        column,
      },
      children: [
        {
          colId: 'product-selected',
          headerName: 'Selected',
          type: 'numericColumn',
          resizable: false,
          // suppressMovable: true,
          suppressMenu: true,
          cellRendererParams: {
            column: {
              id: `${column._id}-select`,
              description: 'Count of products currently selected for submission to TruVoice',
            },
          },
          width: 105,
          onCellClicked: this.handleProductCellClicked,
          valueGetter: (params) => {
            const r = this.productColumnValueGetter(params);
            if (r && r.loaded && !r.allSubmitted && r.isSelected) {
              return r.selectedProductCount;
            }
            return null;
          },
          cellClass: (params) => {
            const r = this.productColumnValueGetter(params);
            if (r && r.loaded && !r.allSubmitted && r.isSelected) {
              const cls = [styles.cellCursor, 'text-right'];
              if (r.selectedProductCount === 0) {
                cls.push('text-danger');
              } else {
                cls.push('text-active');
              }
              return cls.join(' ');
            }
            return null;
          },
          cellRendererSelector: (params) => {
            const r = this.productColumnValueGetter(params);
            if (r && !r.loaded) {
              return {
                component: 'loadingRenderer',
              };
            } else if (r && r.loaded && !r.allSubmitted && r.isSelected && r.selectedProductCount === 0) {
              return {
                component: 'warningRenderer',
                params: {
                  tooltip: 'Select the primary product to gather feedback on to submit this account to TruVoice.',
                },
              };
            }
            return null;
          },
        },
        {
          colId: 'product-available',
          headerName: 'Available',
          type: 'numericColumn',
          resizable: false,
          // suppressMovable: true,
          suppressMenu: true,
          onCellClicked: this.handleCellClicked,
          cellRendererParams: {
            column: {
              id: `${column._id}-available`,
              description: 'Count of products that have NOT been submitted or selected',
            },
          },
          width: 110,
          valueGetter: (params) => {
            const r = this.productColumnValueGetter(params);
            if (r && r.loaded && !r.allSubmitted) {
              return r.availableProductCount;
            }
            return null;
          },
          cellRendererSelector: (params) => {
            const r = this.productColumnValueGetter(params);
            if (r && !r.loaded) {
              return {
                component: 'loadingRenderer',
              };
            }
            return null;
          },
        },
      ],
    };
  }

  getContactColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      // width: column.width,
      hide: !column.is_visible,
      marryChildren: true,
      resizable: false,
      headerClass: 'text-center',
      cellRendererParams: {
        column,
      },
      children: [
        {
          colId: 'contact-selected',
          headerName: 'Selected',
          type: 'numericColumn',
          resizable: false,
          suppressMenu: true,
          cellRendererParams: {
            column: {
              id: `${column._id}-select`,
              description: 'Count of buyers currently selected for submission to TruVoice',
            },
          },
          width: 105,
          onCellClicked: this.handleContactCellClicked,
          valueGetter: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && r.canSubmitMore && r.isSelected) {
              return r.selectedContactCount;
            }
            return null;
          },
          cellClass: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && r.canSubmitMore && r.isSelected) {
              const cls = [styles.cellCursor, 'text-right'];
              if (r.selectedContactCount === 0) {
                cls.push('text-danger');
              } else {
                cls.push('text-active');
              }
              return cls.join(' ');
            } else if (r && r.loaded && !r.canSubmitMore) {
              return styles.disabledCell;
            }
            return 'text-right';
          },
          cellRendererSelector: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && !r.loaded) {
              return {
                component: 'loadingRenderer',
              };
            } else if (r && r.loaded && r.canSubmitMore && r.isSelected && r.selectedContactCount === 0) {
              return {
                component: 'warningRenderer',
                params: {
                  tooltip: 'Select one or more contacts to submit this account to TruVoice.',
                },
              };
            }
            return null;
          },
        },
        {
          colId: 'contact-opportunity',
          headerName: 'Opportunity',
          type: 'numericColumn',
          resizable: false,
          suppressMenu: true,
          onCellClicked: this.handleCellClicked,
          cellRendererParams: {
            column: {
              id: `${column._id}-opportunity`,
              description: 'Count of opportunity buyers that have NOT been selected',
            },
          },
          width: 115,
          valueGetter: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && r.canSubmitMore) {
              return r.opportunityContactCount;
            }
            return null;
          },
          cellRendererSelector: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && !r.loaded) {
              return {
                component: 'loadingRenderer',
              };
            }
            return null;
          },
          cellClass: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && !r.canSubmitMore) {
              return styles.disabledCell;
            }
            return 'text-right';
          },
        },
        {
          colId: 'contact-account',
          headerName: 'Account',
          type: 'numericColumn',
          resizable: false,
          suppressMenu: true,
          onCellClicked: this.handleCellClicked,
          cellRendererParams: {
            column: {
              id: `${column._id}-account`,
              description: 'Count of account buyers that have NOT been selected',
            },
          },
          width: 100,
          valueGetter: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && r.canSubmitMore) {
              return r.accountContactCount;
            }
            return null;
          },
          cellClass: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && r.loaded && !r.canSubmitMore) {
              return styles.disabledCell;
            }
            return 'text-right';
          },
          cellRendererSelector: (params) => {
            const r = this.contactColumnValueGetter(params);
            if (r && !r.loaded) {
              return {
                component: 'loadingRenderer',
              };
            }
            return null;
          },
        },
      ],
    };
  }

  getSubmittedStatusColumn() {
    return {
      colId: STATUS_COLUMN_ID,
      headerName: '',
      width: 0,
      hide: true,
      field: 'submitted',
      resizable: false,
      suppressMenu: true,
      suppressMovableColumns: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      cellRendererParams: {
        column: {},
      },
    };
  }


  getColDefs() {
    const { tableColumns } = this.props;
    const allColumns = reduce(tableColumns, (columns, tableColumn) => {
      let column = null;
      if (tableColumn.tag === 'name') {
        column = this.getAccountColumn(tableColumn);
      } else if (tableColumn.tag === 'size') {
        column = this.getAmountColumn(tableColumn);
      } else if (tableColumn.tag === 'owner') {
        column = this.getOwnerColumn(tableColumn);
      } else if (tableColumn.tag === 'submitted') {
        column = this.getStatusColumn(tableColumn);
      } else if (tableColumn.tag === 'products') {
        column = this.getProductColumn(tableColumn);
      } else if (tableColumn.tag === 'contacts') {
        column = this.getContactColumn(tableColumn);
      } else {
        column = {
          column: tableColumn,
          customFieldType: CUSTOM_FIELD_TYPES.ACCOUNT,
          params: {
            onCellClicked: this.handleCellClicked,
          },
        };
      }
      if (column) {
        columns.push(column);
      }
      return columns;
    }, []);
    const checkColumn = this.getCheckColumn();
    const cols = concat([checkColumn], allColumns, [this.getActionColumn()], this.getSubmittedStatusColumn());
    return cols;
  }

  refresh(account) {
    if (this.gridApi) {
      const { dynamicColumnIds } = this.state;
      if (account) {
        let rowNode = null;
        this.gridApi.forEachNode((node) => {
          if (node.data && node.data.account_id === account.account_id) {
            rowNode = node;
          }
        });
        if (rowNode) {
          this.gridApi.redrawRows({ rowNodes: [rowNode] });
        }
      } else {
        this.gridApi.refreshCells({ force: true, columns: [...dynamicColumnIds, 'ag-Grid-AutoColumn'] });
      }
    }
  }
  productColumnValueGetter(params) {
    const { selectedAccounts, program } = this.props;
    if (!params || !params.data) {
      return null;
    }
    const products = program && program.initiatives && program.initiatives.products ? program.initiatives.products : [];

    const selectedAccount = selectedAccounts[params.data.account_id];
    const isSelected = selectedAccount !== undefined;
    const { tracking } = params.data;
    const loaded = tracking !== undefined;
    const allSubmitted = loaded && tracking && tracking.allSubmitted;

    const allProductIds = map(products, 'id');

    const selectedProductIds = selectedAccount && selectedAccount.selected_product_ids ? intersection(allProductIds, selectedAccount.selected_product_ids) : [];
    const availableProductIds = difference(allProductIds, selectedProductIds);

    const availableProductCount = availableProductIds.length;
    const selectedProductCount = selectedProductIds.length;
    return {
      allSubmitted,
      loaded,
      availableProductCount,
      selectedProductCount,
      isSelected,
      totalProductCount: availableProductCount,
      selectedObject: selectedAccount,
    };
  }

  contactColumnValueGetter(params) {
    const { selectedAccounts } = this.props;
    if (!params || !params.data) {
      return null;
    }
    const account = params.data;
    const selectedAccount = selectedAccounts[account.account_id];
    const { opportunityContacts, accountContacts } = account;
    const selectedContacts = selectedAccount ? selectedAccount.selected_contacts : [];
    
    const selectedContactIds = map(selectedContacts, 'contact_id');
    const opportunityContactIds = difference(map(opportunityContacts, 'contact_id'), selectedContactIds);
    const accountContactIds = difference(map(accountContacts, 'contact_id'), selectedContactIds.concat(opportunityContactIds));

    
    const isSelected = selectedAccount !== undefined;
    const accountContactCount = accountContactIds.length;
    const opportunityContactCount = opportunityContactIds.length;
    const selectedContactCount = selectedContactIds.length;
    const { tracking } = account;
    const canSubmitMore = tracking && !tracking.allSubmitted;
    const loaded = tracking && tracking !== null && tracking !== undefined;

    return {
      canSubmitMore,
      loaded,
      isSelected,
      accountContactCount,
      opportunityContactCount,
      selectedContactCount,
      selectedObject: selectedAccount,
    };
  }

  handleProductCellClicked(params) {
    const r = this.productColumnValueGetter(params);
    if (r && r.loaded && !r.allSubmitted) {
      this.props.requestshowEditProduct(r.selectedObject);
    }
  }
  handleContactCellClicked(params) {
    const r = this.contactColumnValueGetter(params);
    if (r && r.loaded && r.canSubmitMore) {
      this.props.requestshowEditContacts(r.selectedObject);
    }
  }

  showDispositionsDetail(opportunity) {
    this.props.requestShowDispositionsDetail(opportunity);
  }

  hanldeToggleAll() {
    const { disabled } = this.props;
    if (disabled) {
      return;
    }
    this.props.requestToggleAll()
      .then(() => { this.refresh(); });
  }

  handleCellClicked(params) {
    const { disabled } = this.props;
    if (!params || !params.data || disabled) {
      return;
    }
    const { selectedAccounts } = this.props;
    const value = this.checkboxValueGetter(params);
    const accountId = params.data.account_id;
    const selectedOpportunity = selectedAccounts[accountId];
    const isSelected = selectedOpportunity !== null && selectedOpportunity !== undefined;
    const silent = true;
    const colId = params.column ? params.column.colId : null;
    if (value === NONE_SUBMIT || value === PARTIAL_SUBMIT) {
      if (isSelected) {
        if (!silent || colId === 'checkbox') {
          // do not deselect, let user deselect via the checkbox
          this.props.requestUnselectAccount(params.data)
            .then(() => { this.refresh(params.data); });
        }
      } else {
        this.props.requestSelectAccount(params.data)
          .then(() => { this.refresh(params.data); });
      }
    }
  }
  handleRequestSelectAccount(account) {
    const p = this.props.requestSelectAccount(account);
    p.then(() => { 
      this.refresh(account);
      const { selectedAccounts } = this.props;
      const selectedAccount = selectedAccounts[account.account_id];
      if (selectedAccount) {
        this.props.requestEditAccount(selectedAccount);
      }
    });
    return p;
  }

  handleRequestDeselectAccount(account) {
    this.props.requestUnselectAccount(account)
      .then(() => { this.refresh(account); });
  }

  handleRequestShowLogs(loggedAccount) {
    this.setState({ loggedAccount });
  }

  handleFilterSelect(option) {
    this.setState({ status: option.value }, () => {
      if (this.gridApi) {
        const columnFilter = this.gridApi.getFilterInstance(STATUS_COLUMN_ID);
        if (columnFilter) {
          const { status } = this.state;
          if (status) {
            const v = status === 'submitted';
            columnFilter.selectNothing();
            columnFilter.selectValue(v);
          } else {
            columnFilter.selectEverything();
          }
          this.gridApi.onFilterChanged();
        }
      }
    });
  }

  checkboxValueGetter(params) {
    if (params && params.data) {
      const { tracking } = params.data;
      const loaded = tracking !== undefined;
      if (!loaded) {
        return null;
      }
      const submitted = loaded && tracking.submittedContactIds && tracking.submittedContactIds.length > 0;
      const canSubmitMore = tracking && !tracking.allSubmitted;
      if (canSubmitMore) {
        if (submitted) {
          return PARTIAL_SUBMIT;
        }
        return NONE_SUBMIT;
      }
      return ALL_SUBMIT;
    }
    return null; // export to excel with empty
  }

  renderStatusToggle() {
    const { status } = this.state;
    const items = FILTER_OPTIONS;

    const activeItem = find(items, { value: status }) || first(items);

    return (
      <span className="sub-header-with-controls">
        Show {' '}
        <Dropdown id="submit-status-dropdown">
          <Dropdown.Toggle bsStyle="link" bsSize="sm">
            {activeItem === undefined && <span>All Opportunities</span>}
            {activeItem !== undefined && <span>{activeItem.label}</span>}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {items.map((item) => {
              const isActive = activeItem !== undefined && activeItem.value === item.value;
              return (
                <MenuItem
                  eventKey={item}
                  active={isActive}
                  key={item.value}
                  onSelect={this.handleFilterSelect}
                >
                  {item.label}
                </MenuItem>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      </span>
    );
  }


  render() {
    const {
      accounts, topControls, program,
    } = this.props;

    const columnDefs = this.getColDefs();

    return (
      <div>
        <LogModal
          object={this.state.loggedAccount}
          program={program}
          isAccount
          onClose={() => { this.setState({ loggedAccount: null }); }}
        />
        <GodTable
          id={this.props.tableName}
          columnDefs={columnDefs}
          rowData={accounts}
          onGridReady={this.onGridReady}
          leftControls={this.renderStatusToggle()}
          renderStatusToggle
          frameworkComponents={{
            accountNameRenderer: AccountNameRenderer,
            saleRepsNameRenderer: SaleRepsNameRenderer,
            checkboxRenderer: CheckboxRenderer,
            checkboxHeaderRenderer: CheckboxHeaderRenderer,
            actionRenderer: ActionRenderer,
            contactRenderer: ContactRenderer,
            warningRenderer: WarningRenderer,
            loadingRenderer: LoadingRenderer,
            moneyRenderer: MoneyRenderer,
            submittedRenderer: SubmittedRenderer,
            agTotalRowCountComponent: StatusRowComponent,
          }}
          columnTypes={{
            dateColumn: {
              filter: 'agDateColumnFilter',
              filterParams: {
                comparator: dateComparator,
              },
            },
          }}
          enableFullScreen
          tableManageConfigs={{
            enableCustomMeta: true,
            validObjects: ['Account'],
            validCustomMetaObjects: ['Account'],
          }}
          getRowClass={this.getRowClass}
          resourceType={GodTable.RESOURCE_TYPE.ACCOUNT}
          topControls={topControls}
        />
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  return {
    tableColumns: getTablePreference(state, ownProps.tableName),
  };
}

AccountTable.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedAccounts: PropTypes.shape({}).isRequired,
  program: PropTypes.shape({}).isRequired,
  masterCheckboxStatus: PropTypes.string.isRequired,
  tableColumns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    description: PropTypes.string,
    is_system: PropTypes.bool,
    is_visible: PropTypes.bool,
    position: PropTypes.number,
    width: PropTypes.number,
    tag: PropTypes.string,
    label: PropTypes.string,
    target_object: PropTypes.string,
    target_field: PropTypes.string,
  })),
  tableName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  requestShowDispositionsDetail: PropTypes.func,
  requestshowEditProduct: PropTypes.func,
  requestEditAccount: PropTypes.func,
  requestshowEditContacts: PropTypes.func,
  editingAccount: PropTypes.shape({
    account_id: PropTypes.string,
  }),
  requestSelectAccount: PropTypes.func,
  requestUnselectAccount: PropTypes.func,
  requestToggleAll: PropTypes.func,
  topControls: PropTypes.node,
  countSelected: PropTypes.number,
};
AccountTable.defaultProps = {
  tableColumns: [],
  disabled: false,
  requestShowDispositionsDetail: () => {},
  requestshowEditProduct: () => {},
  editingAccount: null,
  requestEditAccount: () => {},
  requestshowEditContacts: () => {},
  requestSelectAccount: () => {},
  requestUnselectAccount: () => {},
  requestToggleAll: () => {},
  topControls: null,
  countSelected: 0,
};
AccountTable.contextTypes = {
};


export default connect(mapStateToProps)(AccountTable);
